import { z } from "zod";
import type { MeasureItemDef } from "./measure-items";
import type { RetailPriceList, RoofInput } from "./retail";
import { type LaborBasis, type LaborItem, type WorkOrderLine, lineTotalCents } from "./work-orders";

/**
 * Roof measurement reports (Roofr, Hover, EagleView) and the material orders built from them
 * (spec §8). Areas are whole square feet, lengths whole feet (rounded up when read), squares
 * thousandths (28.33 SQ = 28330). Order quantities are whole units, always rounded up.
 */

export const MEASUREMENT_SOURCES = ["roofr", "hover", "eagleview", "other"] as const;
export type MeasurementSource = (typeof MEASUREMENT_SOURCES)[number];
export const MEASUREMENT_SOURCE_LABELS: Record<MeasurementSource, string> = { roofr: "Roofr", hover: "Hover", eagleview: "EagleView", other: "Other" };

export interface MeasurementReading {
  source: MeasurementSource;
  address: string;
  /** Total roof area, square feet (no waste). */
  totalAreaSqFt: number;
  /** Area by pitch ("8/12"), square feet. Should add up to the total. */
  pitches: { pitch: string; areaSqFt: number }[];
  facets: number | null;
  ridgeFt: number;
  hipFt: number;
  valleyFt: number;
  eaveFt: number;
  rakeFt: number;
  stepFlashingFt: number;
  wallFlashingFt: number;
  /** The waste the report suggests (whole percent), if it says. */
  suggestedWastePercent: number | null;
  /** The company's extra fields (Settings → Materials → Measurement items): vents, pipe flashings... */
  items?: { key: string; quantity: number }[];
  /** Where a number came from (by field), e.g. a scope's line: "Line 11: R&R Valley metal - 162.00 LF". */
  sources?: Record<string, string>;
}

const ft = z.number().min(0).max(1_000_000);
export const measurementReadingSchema = z.object({
  source: z.enum(MEASUREMENT_SOURCES),
  address: z.string().trim().max(300),
  totalAreaSqFt: ft,
  pitches: z.array(z.object({ pitch: z.string().trim().max(10), areaSqFt: ft })).max(40),
  facets: z.number().int().min(0).max(1000).nullable(),
  ridgeFt: ft,
  hipFt: ft,
  valleyFt: ft,
  eaveFt: ft,
  rakeFt: ft,
  stepFlashingFt: ft,
  wallFlashingFt: ft,
  suggestedWastePercent: z.number().int().min(0).max(50).nullable(),
  items: z.array(z.object({ key: z.string().regex(/^[a-z0-9_]{1,40}$/), quantity: z.number().min(0).max(100_000) })).max(60).optional(),
  sources: z.record(z.string().max(60), z.string().max(300)).optional(),
});

/** Whole numbers from what the reader returned: areas rounded, lengths rounded up; missing → 0. */
export function cleanReading(raw: Partial<Record<keyof MeasurementReading | "perimeterFt", unknown>>): MeasurementReading {
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : typeof v === "string" && Number.isFinite(Number(v.replace(/,/g, ""))) ? Math.max(0, Number(v.replace(/,/g, ""))) : 0);
  const up = (v: unknown) => (num(v) ? Math.ceil(num(v) - 1e-9) : 0);
  const source = MEASUREMENT_SOURCES.includes(raw.source as MeasurementSource) ? (raw.source as MeasurementSource) : "other";
  const pitches = Array.isArray(raw.pitches) ? (raw.pitches as { pitch?: unknown; areaSqFt?: unknown }[]).map((p) => ({ pitch: normalizePitch(String(p?.pitch ?? "")), areaSqFt: Math.round(num(p?.areaSqFt)) })).filter((p) => p.pitch && p.areaSqFt > 0) : [];
  const waste = num(raw.suggestedWastePercent);
  return {
    source,
    address: typeof raw.address === "string" ? raw.address.trim().slice(0, 300) : "",
    totalAreaSqFt: Math.round(num(raw.totalAreaSqFt)),
    pitches,
    facets: num(raw.facets) ? Math.round(num(raw.facets)) : null,
    ridgeFt: up(raw.ridgeFt),
    hipFt: up(raw.hipFt),
    valleyFt: up(raw.valleyFt),
    eaveFt: up(raw.eaveFt),
    rakeFt: up(raw.rakeFt),
    stepFlashingFt: up(raw.stepFlashingFt),
    wallFlashingFt: up(raw.wallFlashingFt),
    suggestedWastePercent: waste && waste <= 50 ? Math.round(waste) : null,
    // Only a total perimeter (eaves and rakes not split): it goes on the eaves, and says so.
    ...(!num(raw.eaveFt) && !num(raw.rakeFt) && num(raw.perimeterFt) ? { eaveFt: up(raw.perimeterFt), sources: { eaveFt: "Total perimeter (eaves + rakes, not split)" } } : {}),
  };
}

/** "8/12", "8:12", "8" → "8/12"; anything else → "". */
export function normalizePitch(p: string): string {
  const m = p.trim().match(/^(\d{1,2})(?:\s*[/:]\s*12)?$/);
  return m ? `${Number(m[1])}/12` : "";
}

/** Rise per 12 ("8/12" → 8); unknown → 0. */
export const pitchRise = (p: string) => Number(normalizePitch(p).split("/")[0] || 0);

/** What doesn't add up in a reading (the rep checks the report before using it). */
export function readingProblems(r: MeasurementReading): string[] {
  const out: string[] = [];
  if (r.totalAreaSqFt <= 0) out.push("No roof area was found.");
  const sum = r.pitches.reduce((s, p) => s + p.areaSqFt, 0);
  // Reports round each pitch's area: allow 1% (and at least 5 sq ft).
  if (r.pitches.length && Math.abs(sum - r.totalAreaSqFt) > Math.max(5, r.totalAreaSqFt / 100)) out.push(`The areas by pitch add up to ${sum.toLocaleString("en-US")} sq ft, not the ${r.totalAreaSqFt.toLocaleString("en-US")} total.`);
  if (r.totalAreaSqFt > 0 && r.eaveFt + r.rakeFt === 0) out.push("No eave or rake lengths were found.");
  return out;
}

/** Squares (thousandths) for an area plus waste, rounded up to the hundredth of a square. */
export function squaresWithWaste(areaSqFt: number, wastePercent: number): number {
  const tenths = areaSqFt * 10 * (100 + wastePercent); // thousandths of a square × 100
  return Math.ceil(tenths / 1000) * 10;
}

/** A retail roof from a reading: squares with waste, and the steep add-ons by pitch (when the price list has them). */
export function roofFromReading(r: MeasurementReading, wastePercent: number, list: RetailPriceList, productKey: string): RoofInput {
  const addOns: { key: string; quantity: number }[] = [];
  const area = (min: number, max: number) => r.pitches.filter((p) => pitchRise(p.pitch) >= min && pitchRise(p.pitch) <= max).reduce((s, p) => s + p.areaSqFt, 0);
  for (const [key, min, max] of [
    ["steep8", 8, 9],
    ["steep10", 10, 99],
  ] as const) {
    const a = area(min, max);
    // Measured (no waste): the add-on grows with the roof's material squares when it's priced.
    if (a > 0 && list.addOns.some((x) => x.key === key)) addOns.push({ key, quantity: Math.round(a * 10) });
  }
  return { squares: Math.round(r.totalAreaSqFt * 10), wastePercent, ridgeLf: Math.ceil(r.ridgeFt + r.hipFt), starterLf: Math.ceil(r.eaveFt + r.rakeFt), productKey, addOns };
}

// ── Material catalog and orders ────────────────────────────────────────────

/** What a catalog item is ordered by. */
export const MATERIAL_BASES = ["squares", "hip_ridge", "eave_rake", "eaves", "valleys", "step_flashing", "manual"] as const;
/** A fixed basis, or one of the company's measurement items ("item:pipe_flashing"). */
export type MaterialBasis = (typeof MATERIAL_BASES)[number] | `item:${string}`;
export const materialBasisSchema = z.union([z.enum(MATERIAL_BASES), z.string().regex(/^item:[a-z0-9_]{1,40}$/)]) as z.ZodType<MaterialBasis>;
/** A basis's name, for one of the company's measurement items too. */
export function basisName(basis: MaterialBasis, items: readonly MeasureItemDef[] = []): string {
  if (basis.startsWith("item:")) {
    const d = items.find((i) => i.key === basis.slice(5));
    return d ? `${d.name} (${d.unit === "ft" ? "ft" : "each"})` : "A measurement item";
  }
  return MATERIAL_BASIS_LABELS[basis as (typeof MATERIAL_BASES)[number]];
}
export const MATERIAL_BASIS_LABELS: Record<(typeof MATERIAL_BASES)[number], string> = {
  squares: "Squares (with waste)",
  hip_ridge: "Hips + ridges (ft)",
  eave_rake: "Eaves + rakes (ft)",
  eaves: "Eaves (ft)",
  valleys: "Valleys (ft)",
  step_flashing: "Step flashing (ft)",
  manual: "Typed in",
};

/**
 * A catalog item (Settings → Materials): mostly ABC Supply items, with NMT's price. For material
 * orders and the estimated material cost only; materials are never QuickBooks items.
 */
/**
 * One orderable ABC item (a color, size, or version of a product): everything ABC needs to take it
 * back on an order: its item number, ABC's own description, the units it accepts, and whether it's
 * cut to length.
 */
export interface MaterialOption {
  itemNumber: string;
  /** ABC's description, exactly (sent back on orders). */
  description: string;
  /** What tells it apart from the product's other options ("Metric · Charcoal"). */
  label: string;
  color: string;
  /** Units ABC accepts for it (stocking first). */
  units: { code: string; name: string }[];
  /** The unit it's ordered and priced in (ABC's stocking unit unless changed). */
  unit: string;
  /** Cut to length: an order line needs a length. */
  dimensional: boolean;
  unitCents: number | null;
  priceSource: "abc" | "last_order" | "manual" | null;
  pricedAt: string | null;
  timesOrdered: number;
  lastOrderedOn: string | null;
  imageUrl: string | null;
}

/**
 * Tells a product's options apart by the words their descriptions don't share ("GAF Timberline
 * Natural Shadow English Shingles Lifetime Barkwood..." → "English Barkwood"); the color when that
 * says nothing; else the item number.
 */
export function optionLabels(options: { itemNumber: string; description: string; color: string }[]): string[] {
  const words = (d: string) => d.split(/\s+/).filter(Boolean);
  const common = options.length > 1 ? words(options[0]!.description).filter((w) => options.every((o) => words(o.description).includes(w))) : [];
  const labels = options.map((o) => {
    const rest = words(o.description).filter((w) => !common.includes(w)).join(" ");
    return rest || (o.color && o.color !== "N/A" ? o.color : "") || o.description || o.itemNumber;
  });
  // Still the same (descriptions identical): add the item number.
  return labels.map((l, i) => (labels.indexOf(l) !== i || labels.lastIndexOf(l) !== i ? `${l} (#${options[i]!.itemNumber})` : l));
}

export interface MaterialItem {
  id: string;
  /** The default option's ABC item number (the most ordered); null when typed in by hand. */
  abcItemNumber: string | null;
  /** ABC's product family (all its colors and sizes); null for a single item or one typed in. */
  abcFamilyId: string | null;
  /** ABC's category and the level below it ("Steep Slope Roofing" › "Fiberglass Laminated Shingles"). */
  category: string;
  subcategory: string;
  /** The orderable options (ABC items); empty when typed in by hand. */
  options: MaterialOption[];
  name: string;
  description: string;
  /** ABC's unit code: BD, RL, PC, SQ, BX... */
  unit: string;
  /** NMT's price for one unit; null: not priced yet. */
  unitCents: number | null;
  priceSource: "abc" | "last_order" | "manual" | null;
  pricedAt: string | null;
  basis: MaterialBasis;
  /** How much of the basis `per` units cover: 3 bundles per 1 square → coverage 1 SQ, per 3. Thousandths. */
  coverage: number;
  per: number;
  imageUrl: string | null;
  timesOrdered: number;
  lastOrderedOn: string | null;
}

export const materialItemInputSchema = z.object({
  abcItemNumber: z.string().trim().min(1).max(40).nullable().default(null),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(300).default(""),
  unit: z.string().trim().min(1).max(10),
  unitCents: z.number().int().min(0).max(10_000_000).nullable().default(null),
  basis: materialBasisSchema.default("manual"),
  coverage: z.number().int().min(1).max(100_000_000).default(1000),
  per: z.number().int().min(1).max(1000).default(1),
});
export type MaterialItemInput = z.infer<typeof materialItemInputSchema>;

/** A template line: a fixed quantity, or counted from the job's measurement by the item's basis. */
export interface MaterialTemplateLine {
  itemId: string;
  mode: "fixed" | "measure";
  /** For "fixed": how many. */
  quantity: number;
  /** Which option (ABC item number) every order gets; null: picked on each order (a product with one option needs no pick). */
  option?: string | null;
}
export interface MaterialTemplate {
  id: string;
  name: string;
  /** The retail price list shingle it's for (proposal cost estimates); empty: none. */
  productKey: string;
  lines: MaterialTemplateLine[];
}
export const materialTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  productKey: z.string().max(40).default(""),
  lines: z
    .array(z.object({ itemId: z.string().uuid(), mode: z.enum(["fixed", "measure"]), quantity: z.number().int().min(0).max(100_000).default(0), option: z.string().max(40).nullable().default(null) }))
    .max(100),
});

/** A template drafted from one ABC order, to look over before saving. */
export interface AbcOrderTemplateDraft {
  name: string;
  lines: MaterialTemplateLine[];
  /** Products the order had that weren't in the catalog: added now. */
  added: string[];
  /** Order lines left out, and why (delivery charges, items ABC no longer has). */
  skipped: string[];
}
export const abcOrderNumberSchema = z.object({ orderNumber: z.string().trim().regex(/^\d{6,14}(-\d+)?$/, "That isn't an ABC order number (like 2009502268).") });

/** A product found in ABC's order history (a family, or a single item), with the options bought. */
export interface AbcHistoryProduct {
  /** The family id, or "item:<number>" for an item without one. */
  key: string;
  name: string;
  category: string;
  subcategory: string;
  bought: { itemNumber: string; description: string; color: string; unit: string; orders: number; quantity: number; lastUnitCents: number | null; lastOrderedOn: string | null }[];
  orders: number;
  /** Already in the catalog. */
  inCatalog: boolean;
}

/** Sorted for picking: category, then the level below, then name (A to Z); unknown categories last. */
export function sortByCategory<T extends { category: string; subcategory: string; name: string }>(list: T[]): T[] {
  const k = (s: string) => (s ? s.toLowerCase() : "\uffff");
  return [...list].sort((a, b) => k(a.category).localeCompare(k(b.category)) || k(a.subcategory).localeCompare(k(b.subcategory)) || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

/** One item found in ABC's order history: how often it was ordered, and the last price paid. */
export interface AbcHistoryItem {
  itemNumber: string;
  description: string;
  unit: string;
  orders: number;
  quantity: number;
  lastUnitCents: number | null;
  lastOrderedOn: string | null;
  /** Already in the catalog. */
  inCatalog: boolean;
}

/** ABC's order history lines, summed per item: order count, total quantity, the newest price paid. */
export function summarizeHistory(orders: { orderedOn: string | null; lines: { itemNumber: string; quantity: number; unit: string; unitPrice: number | null }[] }[]): Omit<AbcHistoryItem, "description" | "inCatalog">[] {
  const out = new Map<string, Omit<AbcHistoryItem, "description" | "inCatalog">>();
  for (const o of [...orders].sort((a, b) => (a.orderedOn ?? "").localeCompare(b.orderedOn ?? ""))) {
    const seen = new Set<string>();
    for (const l of o.lines) {
      if (!l.itemNumber) continue;
      const had = out.get(l.itemNumber) ?? { itemNumber: l.itemNumber, unit: l.unit, orders: 0, quantity: 0, lastUnitCents: null, lastOrderedOn: null };
      if (!seen.has(l.itemNumber)) had.orders++;
      seen.add(l.itemNumber);
      had.quantity += l.quantity;
      // Oldest first, so the last one seen is the newest price.
      if (l.unitPrice !== null && l.unitPrice > 0) {
        had.lastUnitCents = Math.round(l.unitPrice * 100);
        had.unit = l.unit || had.unit;
      }
      had.lastOrderedOn = o.orderedOn ?? had.lastOrderedOn;
      out.set(l.itemNumber, had);
    }
  }
  return [...out.values()].sort((a, b) => b.orders - a.orders || b.quantity - a.quantity);
}

/** The basis amount (thousandths) a reading gives for an item. */
export function basisAmount(basis: MaterialBasis, r: MeasurementReading, wastePercent: number): number {
  if (basis.startsWith("item:")) return Math.round((r.items?.find((i) => i.key === basis.slice(5))?.quantity ?? 0) * 1000);
  switch (basis as (typeof MATERIAL_BASES)[number]) {
    case "squares":
      return squaresWithWaste(r.totalAreaSqFt, wastePercent);
    case "hip_ridge":
      return (r.hipFt + r.ridgeFt) * 1000;
    case "eave_rake":
      return (r.eaveFt + r.rakeFt) * 1000;
    case "eaves":
      return r.eaveFt * 1000;
    case "valleys":
      return r.valleyFt * 1000;
    case "step_flashing":
      return r.stepFlashingFt * 1000;
    case "manual":
      return 0;
  }
}

export interface MaterialLine {
  /** The catalog item's id (older orders: the old catalog's key); null when typed in. */
  itemKey: string | null;
  /** The ABC item (option) picked; null while a product's option still needs picking. */
  itemNumber?: string | null;
  /** ABC's description of that item, exactly (sent back on the order). */
  abcDescription?: string | null;
  /** Cut-to-length items: the length, in `lengthUom` (ABC's unit, e.g. FT). */
  lengthValue?: number | null;
  lengthUom?: string | null;
  name: string;
  unit: string;
  quantity: number;
  /** The price when it was put on the order (for the estimated cost); null or missing: unpriced. */
  unitCents?: number | null;
  note: string;
}

export const materialLineSchema = z.object({
  itemKey: z.string().max(40).nullable(),
  itemNumber: z.string().trim().max(40).nullable().optional(),
  abcDescription: z.string().trim().max(300).nullable().optional(),
  lengthValue: z.number().min(0).max(100_000).nullable().optional(),
  lengthUom: z.string().trim().max(10).nullable().optional(),
  name: z.string().trim().min(1).max(160),
  unit: z.string().trim().max(10),
  quantity: z.number().int().min(0).max(100_000),
  unitCents: z.number().int().min(0).max(10_000_000).nullable().optional(),
  note: z.string().trim().max(300),
});

/** Units of an item for an amount: amount × per ÷ coverage, rounded up. */
export function unitsFor(item: Pick<MaterialItem, "coverage" | "per">, amount: number): number {
  return amount <= 0 ? 0 : Math.ceil((amount * item.per) / item.coverage);
}

/**
 * A catalog item as an order line, at its current price. With options: the one asked for, or the
 * only one; otherwise none yet (to pick on the order), estimated at the product's usual price.
 */
export function lineFor(item: MaterialItem, quantity: number, option?: string | null): MaterialLine {
  const o = item.options.find((x) => x.itemNumber === option) ?? (item.options.length === 1 ? item.options[0] : undefined);
  if (!item.options.length) return { itemKey: item.id, itemNumber: null, name: item.name, unit: item.unit, quantity, unitCents: item.unitCents, note: "" };
  if (!o) return { itemKey: item.id, itemNumber: null, abcDescription: null, name: item.name, unit: item.unit, quantity, unitCents: item.unitCents, note: "" };
  return { itemKey: item.id, itemNumber: o.itemNumber, abcDescription: o.description, name: optionName(item, o), unit: o.unit, quantity, unitCents: o.unitCents, note: "", ...(o.dimensional ? { lengthValue: null, lengthUom: "FT" } : {}) };
}

/** A line's name with its option ("Timberline Natural Shadow · English Barkwood"). */
export const optionName = (item: Pick<MaterialItem, "name" | "options">, o: Pick<MaterialOption, "label">) => (item.options.length > 1 ? `${item.name} · ${o.label}` : item.name);

/** A line switched to another of its product's options. */
export function withOption(line: MaterialLine, item: MaterialItem, itemNumber: string): MaterialLine {
  const o = item.options.find((x) => x.itemNumber === itemNumber);
  if (!o) return line;
  return { ...line, itemNumber: o.itemNumber, abcDescription: o.description, name: optionName(item, o), unit: o.unit, unitCents: o.unitCents, ...(o.dimensional ? { lengthValue: line.lengthValue ?? null, lengthUom: line.lengthUom ?? "FT" } : { lengthValue: undefined, lengthUom: undefined }) };
}

/**
 * What stops an order going to the supplier: a product with no option picked, an option no longer
 * in the product, or a cut-to-length item without a length. Lines at 0 don't count.
 */
export function orderProblems(lines: MaterialLine[], catalog: MaterialItem[]): string[] {
  const byId = new Map(catalog.map((i) => [i.id, i]));
  const out: string[] = [];
  for (const l of lines) {
    if (!l.quantity || !l.itemKey) continue;
    const item = byId.get(l.itemKey);
    if (!item?.options.length) continue;
    const o = item.options.find((x) => x.itemNumber === l.itemNumber);
    if (!l.itemNumber) out.push(`Pick which ${item.name} (color or size).`);
    else if (!o) out.push(`${l.name}: that option isn't in the catalog anymore; pick again.`);
    else if (o.dimensional && !(l.lengthValue && l.lengthValue > 0)) out.push(`${l.name}: enter the length.`);
  }
  return out;
}

/**
 * An order from a template: fixed lines as they are; measured lines counted from the reading (none
 * without one: they start at 0 to fill in). Items no longer in the catalog are left out.
 */
export function orderFromTemplate(t: Pick<MaterialTemplate, "lines">, catalog: MaterialItem[], r: MeasurementReading | null, wastePercent: number): MaterialLine[] {
  const byId = new Map(catalog.map((i) => [i.id, i]));
  return t.lines.flatMap((l) => {
    const item = byId.get(l.itemId);
    if (!item) return [];
    const quantity = l.mode === "fixed" ? l.quantity : r && item.basis !== "manual" ? unitsFor(item, basisAmount(item.basis, r, wastePercent)) : 0;
    return [lineFor(item, quantity, l.option ?? null)];
  });
}

/** What the lines would cost, and which have no price. */
export function linesCost(lines: Pick<MaterialLine, "name" | "quantity" | "unitCents">[]): { cents: number; unpriced: string[] } {
  let cents = 0;
  const unpriced: string[] = [];
  for (const l of lines) {
    if (!l.quantity) continue;
    if (l.unitCents === null || l.unitCents === undefined) unpriced.push(l.name);
    else cents += l.quantity * l.unitCents;
  }
  return { cents, unpriced };
}

export const MATERIAL_ORDER_STATUSES = ["draft", "ordered", "delivered", "cancelled"] as const;
export type MaterialOrderStatus = (typeof MATERIAL_ORDER_STATUSES)[number];
export const MATERIAL_ORDER_STATUS_LABELS: Record<MaterialOrderStatus, string> = { draft: "Draft", ordered: "Ordered", delivered: "Delivered", cancelled: "Cancelled" };

export const materialOrderSchema = z.object({
  supplier: z.string().trim().max(160),
  lines: z.array(materialLineSchema).max(200),
  deliveryDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  deliveryNotes: z.string().trim().max(2000),
  measurementId: z.string().uuid().nullable(),
});
export type MaterialOrderInput = z.infer<typeof materialOrderSchema>;

export const measurementCreateSchema = z.object({ fileId: z.string().uuid() });
export const measurementUpdateSchema = z.object({ reading: measurementReadingSchema, wastePercent: z.number().int().min(0).max(50), label: z.string().trim().max(80).optional(), notes: z.string().trim().max(1000).optional() });
/** Measurements typed in (no report). */
export const measurementManualSchema = z.object({ reading: measurementReadingSchema, wastePercent: z.number().int().min(0).max(50), label: z.string().trim().max(80).default(""), notes: z.string().trim().max(1000).default("") });

export interface MeasurementItem {
  id: string;
  jobId: string;
  /** A report read for you, typed in, or from the carrier's scope. */
  kind: "report" | "manual" | "scope";
  /** Which roof ("Dwelling Roof"); empty: the whole roof. */
  label: string;
  notes: string;
  /** The one proposals, work orders and material orders use (picked, else the newest). */
  inUse: boolean;
  status: "reading" | "read" | "read_failed";
  file: { id: string; name: string; url: string } | null;
  reading: MeasurementReading | null;
  wastePercent: number;
  problems: string[];
  error: string | null;
  createdAt: string;
  createdBy: string | null;
}

export interface MaterialOrderItem extends MaterialOrderInput {
  id: string;
  number: number;
  jobId: string;
  jobNumber: number;
  customerName: string;
  address: string;
  status: MaterialOrderStatus;
  orderedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  createdBy: string | null;
  pdfUrl: string | null;
  /** Sent to ABC from the CRM (null: not sent, or ordered another way). */
  abc: AbcOrderState | null;
  /** Work orders made from it ("Make work order"). */
  workOrders: { id: string; number: number }[];
}

export interface AbcOrderState {
  confirmation: string | null;
  orderNumber: string | null;
  status: string | null;
  event: string | null;
  checkedAt: string | null;
  sentAt: string | null;
  sentBy: string | null;
  deliveryService: AbcDeliveryService | null;
  deliveryTime: AbcDeliveryTime | null;
  /** The branch it went to. */
  branch: { number: string; name: string } | null;
  /** Sending failed (fix and send again). */
  error: string | null;
  /** ABC didn't answer clearly: it may have the order. */
  unknown: boolean;
}

export const ABC_DELIVERY_SERVICES = ["OTR", "OTG", "CPU"] as const;
export type AbcDeliveryService = (typeof ABC_DELIVERY_SERVICES)[number];
export const ABC_DELIVERY_SERVICE_LABELS: Record<AbcDeliveryService, string> = { OTR: "Delivered on the roof", OTG: "Delivered on the ground", CPU: "We'll pick it up" };
export const ABC_DELIVERY_TIMES = ["AT", "AM", "PM", "FS"] as const;
export type AbcDeliveryTime = (typeof ABC_DELIVERY_TIMES)[number];
export const ABC_DELIVERY_TIME_LABELS: Record<AbcDeliveryTime, string> = { AT: "Any time", AM: "Morning", PM: "Afternoon", FS: "First stop" };
export const abcSendSchema = z.object({
  deliveryService: z.enum(ABC_DELIVERY_SERVICES),
  deliveryTime: z.enum(ABC_DELIVERY_TIMES),
  /** Another of the account's branches for this order; left out: the company's usual one (Settings → Materials). */
  branchNumber: z.string().trim().min(1).max(20).optional(),
});

/** What would go to ABC, to look over before sending. */
export interface AbcOrderPreview {
  /** Can't send until these are fixed. */
  problems: string[];
  purchaseOrder: string;
  jobName: string;
  address: string;
  shipTo: string;
  branch: string;
  /** The branch it goes to (picked, or the usual one). */
  branchNumber: string;
  deliveryDate: string | null;
  deliveryNotes: string;
  contact: { name: string; email: string; phone: string };
  lines: { itemNumber: string; description: string; quantity: number; unit: string; length: string | null; unitCents: number | null; note: string }[];
  /** At ABC's current prices where it gave one, else the order's. */
  totalCents: number;
  /** ABC's sandbox: a test order, not a real one. */
  sandbox: boolean;
}

/**
 * What a work order made from a material order lists for the crew: each line with its quantity and
 * unit, then how and when it's delivered.
 */
export function workOrderMaterials(o: Pick<MaterialOrderItem, "number" | "supplier" | "lines" | "deliveryDate" | "deliveryNotes" | "abc">): string {
  const qty = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, ""));
  const lines = o.lines.filter((l) => l.quantity > 0).map((l) => `${qty(l.quantity)} ${l.unit} ${l.abcDescription || l.name}${l.lengthValue ? ` (${qty(l.lengthValue)} ${l.lengthUom ?? "ft"})` : ""}${l.note ? ` - ${l.note}` : ""}`.replace(/\s+/g, " ").trim());
  const when = o.deliveryDate ? new Date(`${o.deliveryDate}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" }) : "";
  const how = o.abc?.deliveryService ? ABC_DELIVERY_SERVICE_LABELS[o.abc.deliveryService].toLowerCase() : "";
  const delivery = [o.supplier && `From ${o.supplier}`, when && `delivery ${when}`, how, o.abc?.confirmation && `ABC confirmation ${o.abc.confirmation}`].filter(Boolean).join(", ");
  return [...lines, "", `Material order #${o.number}${delivery ? `: ${delivery}` : ""}.`, o.deliveryNotes ? `Delivery notes: ${o.deliveryNotes}` : ""].filter((x, i, a) => x !== "" || (i > 0 && a[i - 1] !== "")).join("\n").trim();
}

/** The purchase order ABC gets: the job's street, as NMT writes them (ABC takes 16 characters). */
export const abcPurchaseOrder = (street: string, jobNumber: number) => (street.trim() || `Job ${jobNumber}`).replace(/\s+/g, " ").slice(0, 16).trim();

/** For the reader: the tool it must call. */
export const MEASUREMENT_TOOL_NAME = "record_measurements";

export const MEASUREMENT_PROMPT = `This is a roof measurement report (Roofr, Hover, EagleView, or similar). Read the report's own totals and call ${MEASUREMENT_TOOL_NAME} with them.
- totalAreaSqFt: the total roof area in square feet, WITHOUT waste (Roofr "Total roof area", EagleView "Total Roof Area", Hover "Roof area" / "Total"). If the report gives only squares, multiply by 100.
- pitches: the area for each pitch from the pitch/area table (e.g. "8/12": 1,234 sq ft). Leave it empty if there is no table.
- ridgeFt, hipFt, valleyFt, eaveFt, rakeFt, stepFlashingFt, wallFlashingFt: total lengths in feet from the report's length summary (eaves may be called "starter"; flashing may be "step flashing" and "wall flashing" / "headwall"). Use 0 when the report doesn't list one.
- perimeterFt: the total perimeter ("Total Perimeter Length") if the report gives one, else 0.
- facets: number of roof facets if listed.
- suggestedWastePercent: the waste percent the report recommends ("suggested" / "recommended"), or null if it doesn't say.
- source: roofr, hover, eagleview, or other. address: the property address on the report.
Read the numbers exactly as printed; don't estimate or add your own waste.`;

export function measurementToolSchema() {
  const n = { type: "number" };
  return {
    type: "object",
    properties: {
      source: { type: "string", enum: [...MEASUREMENT_SOURCES] },
      address: { type: "string" },
      totalAreaSqFt: n,
      pitches: { type: "array", items: { type: "object", properties: { pitch: { type: "string" }, areaSqFt: n }, required: ["pitch", "areaSqFt"] } },
      facets: { type: ["number", "null"] },
      ridgeFt: n,
      hipFt: n,
      valleyFt: n,
      eaveFt: n,
      rakeFt: n,
      stepFlashingFt: n,
      wallFlashingFt: n,
      perimeterFt: n,
      suggestedWastePercent: { type: ["number", "null"] },
    },
    required: ["source", "totalAreaSqFt", "pitches", "ridgeFt", "hipFt", "valleyFt", "eaveFt", "rakeFt"],
  };
}

export interface MaterialCatalogResponse {
  items: MaterialItem[];
  templates: MaterialTemplate[];
  supplier: string;
  wastePercent: number;
  /** The company's extra measurement fields (vents, pipe flashings...). */
  measurementItems: MeasureItemDef[];
}

/** ABC Supply as Settings → Materials shows it. */
export interface AbcStatus {
  /** Keys saved on the server. */
  connected: boolean;
  environment: "sandbox" | "production" | null;
  shipTo: { number: string; name: string; address: string } | null;
  branch: { number: string; name: string } | null;
  /** The ship-to's branches (its home branch first). */
  branches: { number: string; name: string; home: boolean }[];
  autoPrices: boolean;
  pricesCheckedAt: string | null;
  /** Sending orders to ABC from the CRM is switched on. */
  ordering: boolean;
  /** This server may send real orders (production, or ABC's sandbox anywhere). */
  orderingAllowed: boolean;
}

/** One result from searching ABC's catalog. */
export interface AbcSearchItem {
  itemNumber: string;
  description: string;
  familyName: string;
  units: { code: string; name: string }[];
  imageUrl: string | null;
  /** Already in the catalog (its id). */
  catalogId: string | null;
}

/** What "Update prices" did. */
export interface PriceUpdateResult {
  checked: number;
  changed: { id: string; name: string; fromCents: number | null; toCents: number }[];
  /** ABC couldn't price these (e.g. "call for pricing"); their price stays. */
  failed: { id: string; name: string; reason: string }[];
}

// ── Labor from a measurement report (spec §8: labor numbers flow to the sub's work order) ──

/** How much of a labor item a reading gives (thousandths); 0 for typed-in items. */
export function laborQuantity(basis: LaborBasis, r: MeasurementReading, wastePercent: number): number {
  const steep = (min: number, max: number) => r.pitches.filter((p) => pitchRise(p.pitch) >= min && pitchRise(p.pitch) <= max).reduce((s, p) => s + p.areaSqFt, 0);
  switch (basis) {
    case "squares":
      return squaresWithWaste(r.totalAreaSqFt, wastePercent);
    case "steep8": {
      const a = steep(8, 9);
      return a ? squaresWithWaste(a, wastePercent) : 0;
    }
    case "steep10": {
      const a = steep(10, 99);
      return a ? squaresWithWaste(a, wastePercent) : 0;
    }
    case "hip_ridge":
      return (r.hipFt + r.ridgeFt) * 1000;
    case "eave_rake":
      return (r.eaveFt + r.rakeFt) * 1000;
    case "valleys":
      return r.valleyFt * 1000;
    case "step_flashing":
      return r.stepFlashingFt * 1000;
    case "manual":
      return 0;
  }
}

/** Work order labor lines from a reading: each counted item with a quantity, at the sub's rate when they have one. */
export function laborFromReading(r: MeasurementReading, wastePercent: number, items: LaborItem[], subRates: Record<string, number>): Omit<WorkOrderLine, "totalCents">[] {
  return items
    .filter((i) => !i.archived && i.basis !== "manual")
    .map((i) => ({ laborItemId: i.id, description: i.name, unit: i.unit, quantity: laborQuantity(i.basis, r, wastePercent), unitCents: subRates[i.id] ?? i.priceCents }))
    .filter((l) => l.quantity > 0);
}

export const subLaborRatesSchema = z.object({ rates: z.array(z.object({ laborItemId: z.string().uuid(), priceCents: z.number().int().min(0).max(10_000_000).nullable() })).max(200) });

// ── Expected job cost from the report (spec §8: materials from the report + labor) ──

export interface JobCostEstimate {
  materials: { name: string; unit: string; quantity: number; unitCents: number; totalCents: number }[];
  materialsCents: number;
  /** Catalog items on the order with no cost entered yet. */
  unpriced: string[];
  labor: { description: string; quantity: number; unitCents: number; totalCents: number }[];
  laborCents: number;
  totalCents: number;
}

/** Materials (a template's order from the report, at catalog prices) plus labor (counted items at the sub's or list rates). */
export function estimateJobCost(r: MeasurementReading, wastePercent: number, template: Pick<MaterialTemplate, "lines"> | null, catalog: MaterialItem[], laborItems: LaborItem[], rates: Record<string, number>): JobCostEstimate {
  const order = template ? orderFromTemplate(template, catalog, r, wastePercent) : [];
  const materials = order
    .filter((l) => l.quantity > 0)
    .map((l) => ({ name: l.name, unit: l.unit, quantity: l.quantity, unitCents: l.unitCents ?? 0, totalCents: l.quantity * (l.unitCents ?? 0) }));
  const labor = laborFromReading(r, wastePercent, laborItems, rates).map((l) => ({ description: l.description, quantity: l.quantity, unitCents: l.unitCents, totalCents: lineTotalCents(l.quantity, l.unitCents) }));
  const materialsCents = materials.reduce((s, m) => s + m.totalCents, 0);
  const laborCents = labor.reduce((s, l) => s + l.totalCents, 0);
  return { materials, materialsCents, unpriced: materials.filter((m) => !m.unitCents).map((m) => m.name), labor, laborCents, totalCents: materialsCents + laborCents };
}

// ── Price updates from a supplier's spreadsheet (CSV) ──

/** Rows of a CSV file: quoted fields, commas and line breaks inside quotes, "" for a quote. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const s = text.replace(/^﻿/, "");
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (quoted) {
      if (c === '"' && s[i + 1] === '"') (field += '"'), i++;
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") row.push(field), (field = "");
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      (row = []), (field = "");
    } else field += c;
  }
  if (field || row.length) row.push(field), rows.push(row);
  return rows.filter((r) => r.some((f) => f.trim()));
}

/** Cents from a price cell ("$1,234.50", "42", "38.125"); half a cent rounds up. Null if it isn't a price. */
export function priceCents(cell: string): number | null {
  const clean = cell.replace(/[$,\s]/g, "");
  const m = /^(\d+)(?:\.(\d*))?$/.exec(clean);
  if (!m) return null;
  const frac = (m[2] ?? "").padEnd(3, "0");
  // Whole cents from the digits (no floating point): dollars, two decimals, then round on the third.
  return Number(m[1]) * 100 + Number(frac.slice(0, 2)) + (Number(frac[2]) >= 5 ? 1 : 0);
}

const itemKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

export interface PriceSheetResult {
  /** The items whose price changes, with the new price. */
  updated: { id: string; name: string; fromCents: number | null; toCents: number }[];
  /** Sheet rows that matched no catalog item (or more than one). */
  unmatched: string[];
}

/**
 * Reads a supplier price sheet against the catalog. The header row names the columns: an item
 * column ("Item", "Description", "Product", "Name"), a price column ("Cost", "Price", "Unit price"),
 * and optionally the supplier's item number ("Item Number", "SKU", "Code"). Rows match by item
 * number, then by the same name or description, then by one containing the other (only when that's
 * a single item). Throws when the columns can't be found.
 */
export function applyPriceSheet(catalog: Pick<MaterialItem, "id" | "name" | "description" | "abcItemNumber" | "unitCents">[], csv: string): PriceSheetResult {
  const rows = parseCsv(csv);
  const header = (rows[0] ?? []).map((h) => h.trim().toLowerCase());
  const col = (re: RegExp) => header.findIndex((h) => re.test(h));
  // "Item Description" over "Item Number": a column of codes never names the item.
  const codeLike = /number|#|code|sku|\bid\b|no\./;
  const numberCol = header.findIndex((h) => codeLike.test(h));
  const named = (re: RegExp) => header.findIndex((h) => re.test(h) && !codeLike.test(h));
  const nameCol = [named(/description/), named(/name|product|material/), named(/item/)].find((i) => i >= 0) ?? -1;
  const priceCol = col(/cost|price|amount/);
  if (priceCol < 0 || (nameCol < 0 && numberCol < 0)) throw new Error('The first row needs column names: one for the item (like "Item", "Description", or "Item Number") and one for the price (like "Cost" or "Price").');
  const updated = new Map<string, PriceSheetResult["updated"][number]>();
  const unmatched: string[] = [];
  const names = (i: (typeof catalog)[number]) => [itemKey(i.name), itemKey(i.description)].filter(Boolean);
  for (const r of rows.slice(1)) {
    const name = (nameCol >= 0 ? r[nameCol] : "")?.trim() ?? "";
    const number = (numberCol >= 0 ? r[numberCol] : "")?.trim() ?? "";
    const cents = priceCents(r[priceCol] ?? "");
    if (cents === null || (!name && !number)) {
      if (name || number) unmatched.push(`${name || number} (no price)`);
      continue;
    }
    const n = itemKey(name);
    let hits = number ? catalog.filter((i) => i.abcItemNumber && itemKey(i.abcItemNumber) === itemKey(number)) : [];
    if (!hits.length && n) hits = catalog.filter((i) => names(i).includes(n));
    if (!hits.length && n.length >= 4) hits = catalog.filter((i) => names(i).some((x) => x.includes(n) || n.includes(x)));
    if (hits.length !== 1) {
      unmatched.push(name || number);
      continue;
    }
    const item = hits[0]!;
    if (item.unitCents !== cents) updated.set(item.id, { id: item.id, name: item.name, fromCents: item.unitCents, toCents: cents });
  }
  return { updated: [...updated.values()], unmatched };
}

/** Adding picked products from the order history to the catalog, in the background. */
export interface AbcKeepProgress {
  total: number;
  done: number;
  added: number;
  failed: string[];
  startedAt: string;
  /** Null while it's still going. */
  finishedAt: string | null;
}
